import { useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { selectCrossClientLandingSelectedOwnerId } from '../../../../store/crossClient/crossClientLanding'
import { useGetScheduledCrossClientQueriesQuery } from '../../../../store/crossClient/scheduledCrossClient'
import { type ScheduledCrossClientQuery } from '../../../../models/ScheduledCrossClientQuery'

const PAGE_SIZE = 10

export interface CrossClientScheduledResult {
  queries: ScheduledCrossClientQuery[]
  totalCount: number
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  showMore: () => void
}

export const useCrossClientScheduled = (): CrossClientScheduledResult => {
  const accountId = useAppSelector(selectAccountId)
  const selectedOwnerId = useAppSelector(selectCrossClientLandingSelectedOwnerId)

  // Page size is scoped to the owner it was expanded for. Deriving the effective size (rather than
  // resetting via a render-phase setState or an effect) keeps a new owner at PAGE_SIZE while avoiding
  // the cascading-render pitfalls that break react-redux's store subscription.
  const [paging, setPaging] = useState({ ownerId: selectedOwnerId, size: PAGE_SIZE })
  const size = paging.ownerId === selectedOwnerId ? paging.size : PAGE_SIZE

  const { data, isLoading, isFetching } = useGetScheduledCrossClientQueriesQuery({
    accountId,
    pageRequest: { page: 0, size },
    managedById: selectedOwnerId ?? undefined,
  })

  const queries = data?.data ?? []
  const totalCount = data?.page.totalElements ?? queries.length

  return {
    queries,
    totalCount,
    loading: isLoading,
    hasMore: queries.length < totalCount,
    loadingMore: isFetching,
    showMore: () =>
      setPaging((prev) => {
        const base = prev.ownerId === selectedOwnerId ? prev : { ownerId: selectedOwnerId, size: PAGE_SIZE }
        return { ownerId: selectedOwnerId, size: base.size + PAGE_SIZE }
      }),
  }
}
